import { useCallback, useEffect, useRef } from 'react'
import { useWriteContract, useSimulateContract } from 'wagmi'
import { Address, WriteContractReturnType } from 'viem'
import { ethConverter, EthUnit } from 'primsell-shared-layer/helpers'

import {
  NOT_ENOUGH_FUNDS,
  NOT_ENOUGH_FUNDS_SHOW,
  REJECTED_BY_USER,
  REJECTED_BY_USER_SHOW,
} from 'constants/errors.constants'

export interface IContractWrite {
  gasPrice: bigint
  gasLimit: bigint
  address: Address
  functionName: string
  from: `0x${string}`
  abi: any
  args: any
  onError: (error: string) => void
  onWriteSuccess?: (txHash: `0x${string}`) => void
  onTransactionReceipt?: (txHash: string) => void
}

export function ContractWrite({
  address,
  abi,
  functionName,
  from,
  gasPrice,
  gasLimit,
  args,
  onError,
  onWriteSuccess,
}: IContractWrite) {
  const handleError = useCallback(
    (error: Error) => {
      if (error.message.includes(REJECTED_BY_USER)) {
        onError(REJECTED_BY_USER_SHOW)
        return
      }
      if (error.message.includes(NOT_ENOUGH_FUNDS)) {
        onError(NOT_ENOUGH_FUNDS_SHOW)
        return
      }

      onError('You failed to burn NFT\nPlease try again')
    },
    [onError]
  )
  const { error } = useSimulateContract({
    address,
    abi,
    functionName,
    account: from,
    gasPrice: ethConverter(gasPrice, EthUnit.Gwei, EthUnit.Wei),
    gas: gasLimit,
    args,
  })

  const { writeContract } = useWriteContract({
    mutation: {
      onError: handleError,
      onSuccess: (data: WriteContractReturnType | undefined) => {
        data && onWriteSuccess && onWriteSuccess(data)
      },
    },
  })

  const sendRef = useRef(false)

  useEffect(() => {
    if (error) {
      handleError(error)
    }
  }, [error])

  useEffect(() => {
    if (!sendRef.current && writeContract) {
      sendRef.current = true
      writeContract({
        address,
        abi,
        functionName,
        account: from,
        gasPrice: gasPrice,
        gas: gasLimit,
        args,
      })
    }
  }, [writeContract])

  return null
}
